'use client';

import { BigNum } from '@velocity-exchange/sdk';
import React from 'react';
import UnstakeUtilizationCutoffMessage from 'src/components/Earn/InsuranceFundUnstakeUtilizationCutoffMessage';
import { CollateralInputV2 } from 'src/components/Inputs/CollateralInput';
import { InputQuickActions } from 'src/components/Inputs/InputQuickActions';
import Link from 'src/components/primitives/Link/Link';
import UnstakeRequestInlineAlert from 'src/components/Staking/UnstakeRequestInlineAlert';
import {
	StakingFormContentProps,
	UseUnstakeForm,
} from 'src/components/Staking/types';
import Divider from 'src/components/primitives/Divider/Divider';
import dayjs from 'dayjs';
import { STANDARD_DATE_FORMAT } from 'src/constants/dates';
import { getCleanedMarketSymbol } from 'src/utils/market';
import { VELOCITY_DOCS_URL } from 'src/constants/constants';

interface Props
	extends
		Omit<
			UseUnstakeForm,
			'isSubmitDisabled' | 'handleSubmit' | 'submitButtonContent'
		>,
		StakingFormContentProps {}

export default function UnstakeFormContent({
	amount,
	setAmount,
	amountBignum,
	selectedMarket,
	selectedMarketStakeData,
	excludedMarkets,
	hasExistingUnstakeRequest,
	isVelocityStaking,
	unstakingPeriodAsDays,
	canWithdraw,
	withdrawPaused,
	newWithdrawalDate,
	existingUnstakeRequestAmount,
	existingUnstakeRequestTimestamp,
	unstakeUtilizationCutoffResult,
	displayPrecision,
	isWalletConnected,
	isSubmitting,
	hideUnstakeRequest,
	handleAmountChange,
	handleMaxAmountClick,
	handleSelectedMarketChange,
	handleCancelUnstakeRequest,
	handleWithdrawUnstakedAssets,
}: Props) {
	return (
		<>
			<CollateralInputV2
				selectedMarket={selectedMarket}
				maxAmount={
					selectedMarketStakeData?.userStake?.currentStakeBigNum ||
					BigNum.zero(selectedMarket.precisionExp)
				}
				onChangeMarket={handleSelectedMarketChange}
				label={
					isVelocityStaking
						? 'Amount to unstake'
						: 'Vault and amount to unstake'
				}
				value={amount}
				onChangeValue={handleAmountChange}
				onMax={handleMaxAmountClick}
				amountLabel="Your stake"
				disabled={hasExistingUnstakeRequest}
				excludedMarketIndices={excludedMarkets}
				banks={[selectedMarket]}
				source={{ type: 'ifStake' }}
				customElementRight={
					<InputQuickActions.SizePresets
						onClick={(presetAmount) => setAmount(presetAmount.print())}
						maxAmount={
							selectedMarketStakeData?.userStake?.currentStakeBigNum ||
							BigNum.zero(selectedMarket.precisionExp)
						}
					/>
				}
				isFixedAsset
			/>

			<div className="mt-auto flex flex-col gap-y-sm">
				<div className="flex flex-col gap-1">
					<div className="flex items-center justify-between gap-1">
						<span className="typo-body-xs text-text-secondary">
							New Staked Balance
						</span>
						<span className="typo-body-xs text-text-default">
							{hasExistingUnstakeRequest
								? (
										selectedMarketStakeData?.userStake?.currentStakeBigNum ||
										BigNum.zero(selectedMarket.precisionExp)
									).toFixed(displayPrecision)
								: (
										selectedMarketStakeData?.userStake?.currentStakeBigNum ||
										BigNum.zero(selectedMarket.precisionExp)
									)
										.sub(amountBignum)
										.toFixed(displayPrecision)}{' '}
							{getCleanedMarketSymbol(selectedMarket.symbol)}
						</span>
					</div>
					<div className="flex items-center justify-between gap-1">
						<span className="typo-body-xs text-text-secondary">
							New Requested for Unstake
						</span>
						<span className="typo-body-xs text-text-default">
							{hasExistingUnstakeRequest
								? '0'
								: amountBignum?.toFixed(displayPrecision)}{' '}
							{getCleanedMarketSymbol(selectedMarket.symbol)}
						</span>
					</div>
				</div>

				{unstakeUtilizationCutoffResult.unstakingIsDisabled && (
					<UnstakeUtilizationCutoffMessage
						{...unstakeUtilizationCutoffResult}
						type="UnstakeModal"
					/>
				)}

				{!hideUnstakeRequest && (
					<UnstakeRequestInlineAlert
						hasExistingUnstakeRequest={hasExistingUnstakeRequest}
						canWithdraw={canWithdraw && !withdrawPaused}
						existingUnstakeRequestAmount={existingUnstakeRequestAmount}
						existingUnstakeRequestTimestamp={existingUnstakeRequestTimestamp}
						selectedMarket={selectedMarket}
						isSubmitting={isSubmitting}
						handleWithdrawUnstakedAssets={handleWithdrawUnstakedAssets}
						handleCancelUnstakeRequest={handleCancelUnstakeRequest}
						displayPrecision={displayPrecision}
					/>
				)}
			</div>

			{isWalletConnected && (
				<>
					<Divider />
					<div className="flex flex-col gap-y-xs typo-body-xs text-text-secondary">
						<p>
							Funds will be available to withdraw on{' '}
							{dayjs(newWithdrawalDate).format(STANDARD_DATE_FORMAT)}.
						</p>
						<p>
							You can only have one pending unstake request per vault at a time.
							You can cancel a request at any time, noting your{' '}
							{unstakingPeriodAsDays} day cool down period will restart upon any
							new unstake request.
						</p>
						<Link
							variant="secondary"
							href={`${VELOCITY_DOCS_URL}/insurance-fund/insurance-fund-staking`}
							openInNewTab
						>
							Learn more
						</Link>
					</div>
				</>
			)}
		</>
	);
}
